import { useState, useRef, useEffect } from "react";
import { Send, Bot, User as UserIcon, Loader2, Sparkles, Mail } from "lucide-react";
import { Navbar, Footer } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Como me cadastro como profissional?",
  "Quanto custa para manter meu perfil ativo?",
  "Como anuncio um imóvel para alugar?",
  "Como me torno anunciante oficial?",
];

export default function FaleConosco() {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Olá! 👋 Sou o assistente do Portal Brasil Portugal. Como posso te ajudar hoje?" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/contact/chat", { session_id: sessionId, message: msg });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erro ao enviar mensagem.");
      setMessages((m) => [...m, { role: "assistant", content: "Desculpe, tive um problema para responder. Pode tentar de novo?" }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafaf8] flex flex-col">
      <Navbar />

      <div className="max-w-3xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10 flex-1 flex flex-col">
        <div className="text-center mb-6">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-[#1b5e3f] bg-[#e8f0ec] px-3 py-1.5 rounded-full"><Sparkles className="w-3.5 h-3.5" /> Assistente com IA</span>
          <h1 className="font-head text-3xl sm:text-4xl font-bold text-[#1c1917] mt-4">Fale Conosco</h1>
          <p className="text-[#78716c] mt-2">Tire suas dúvidas sobre o portal em tempo real com o nosso assistente.</p>
        </div>

        <div className="flex-1 bg-white rounded-2xl border border-[#e7e5e4] shadow-sm flex flex-col overflow-hidden">
          <div data-testid="chat-messages" className="flex-1 overflow-y-auto p-5 space-y-4 min-h-[340px] max-h-[52vh]">
            {messages.map((m, i) => (
              <div key={i} data-testid={`chat-msg-${m.role}`} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`w-9 h-9 rounded-xl shrink-0 flex items-center justify-center ${m.role === "user" ? "bg-[#f59e0b]" : "bg-[#1b5e3f]"}`}>
                  {m.role === "user" ? <UserIcon className="w-4 h-4 text-white" /> : <Bot className="w-4 h-4 text-white" />}
                </div>
                <div className={`rounded-2xl px-4 py-2.5 max-w-[80%] text-sm leading-relaxed whitespace-pre-line ${m.role === "user" ? "bg-[#f59e0b] text-white rounded-tr-sm" : "bg-[#f5f5f3] text-[#1c1917] rounded-tl-sm"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#1b5e3f] flex items-center justify-center"><Bot className="w-4 h-4 text-white" /></div>
                <div className="rounded-2xl px-4 py-3 bg-[#f5f5f3] text-[#78716c] flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Digitando...</div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {messages.length <= 1 && (
            <div className="px-5 pb-3 flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <button key={s} data-testid="chat-suggestion" onClick={() => send(s)} className="text-xs bg-[#fafaf8] border border-[#e7e5e4] hover:border-[#1b5e3f] hover:text-[#1b5e3f] text-[#78716c] rounded-full px-3 py-1.5 transition-colors">{s}</button>
              ))}
            </div>
          )}

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-[#e7e5e4] p-3 flex items-center gap-2">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Escreva a sua mensagem..."
              className="flex-1 bg-[#fafaf8] border border-[#e7e5e4] rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b5e3f]/20 focus:border-[#1b5e3f]"
            />
            <Button data-testid="chat-send-btn" type="submit" disabled={loading || !input.trim()} className="rounded-xl bg-[#1b5e3f] hover:bg-[#13422c] h-12 w-12 p-0">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </Button>
          </form>
        </div>

        <p className="text-center text-xs text-[#78716c] mt-4 flex items-center justify-center gap-1">
          <Mail className="w-3.5 h-3.5" /> Para assuntos urgentes, deixe seu e-mail na conversa que a equipe entra em contato.
        </p>
      </div>

      <Footer />
    </div>
  );
}
